import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSlideOverlay } from '@/hooks/useSlideOverlay';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Icon } from '../Icon/Icon';
import { nb } from '@/i18n/nb';
import s from './Overlay.module.css';

type OverlayProps = {
  title: string;
  onClose: () => void;
  /**
   * When set, the back button + Esc step here instead of closing — e.g. a nested
   * sub-card returning to its overview (Iter 5). Drag-to-dismiss still closes fully.
   */
  onBack?: () => void;
  children: ReactNode;
};

/*
 * Full-screen slide-up overlay used to open a module from the landing.
 * Drag-to-dismiss on touch viewports, back-button on desktop (both wired here),
 * with a hand-built focus trap + Esc-to-close (CLAUDE.md "A11y is manual").
 */
export function Overlay({ title, onClose, onBack, children }: OverlayProps) {
  const titleId = useId();
  const { overlayRef, backdropRef, requestClose, dragHandlers } = useSlideOverlay(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  // Merge the slide-overlay ref and the focus-trap ref onto the same node.
  const setOverlayNode = (node: HTMLDivElement | null) => {
    overlayRef.current = node;
    trapRef.current = node;
  };

  // "Dismiss" steps back to onBack when provided (sub-card → overview), otherwise it
  // animates the whole overlay closed. A ref keeps the Esc listener bound to the latest.
  const dismiss = () => {
    if (onBack) onBack();
    else requestClose();
  };
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') dismissRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return createPortal(
    <>
      <div ref={backdropRef} className={s.backdrop} onPointerDown={() => requestClose()} />
      <div
        ref={setOverlayNode}
        className={s.overlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...dragHandlers}
      >
        <div className={s.bar}>
          <span className={s.grip} aria-hidden="true" />
          <button type="button" className={s.back} onClick={dismiss}>
            <Icon name="back" size={18} />
            {nb.common.back}
          </button>
          <h2 id={titleId} className={s.title}>
            {title}
          </h2>
        </div>
        <div className={s.body}>
          <div className={s.inner}>{children}</div>
        </div>
      </div>
    </>,
    document.body,
  );
}
